import { useEffect, useState } from "react";
import {
  DEFAULT_GRACE_DAYS,
  SUBSCRIPTION_LABELS,
  SUBSCRIPTION_STATES,
} from "@dentacore/shared-types";
import type { SubscriptionState } from "@dentacore/shared-types";

import { Select } from "../ui/select";
import styles from "./operator-page.module.css";
import { clinicSubscription, saveSubscription } from "./operator-api";
import type { SubscriptionView } from "./operator-api";
import { RequiredMark } from "../ui/required-mark";

/**
 * **Where this clinic stands with its subscription** (STORY-190b, BL-D10).
 *
 * Moved **by hand only**: nothing changes when a date passes, so a late bank
 * transfer never cuts a practice off. Read-only means the whole clinic can still
 * be read and nothing can be changed (BL-D11). Every move is recorded with who and
 * when and an optional reason.
 */
export function SubscriptionPanel({ clinicId }: { clinicId: string }): React.JSX.Element {
  const [view, setView] = useState<SubscriptionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<SubscriptionState>("ACTIVE");
  const [dueOn, setDueOn] = useState("");
  const [graceDays, setGraceDays] = useState(String(DEFAULT_GRACE_DAYS));
  const [reason, setReason] = useState("");

  const load = (next: SubscriptionView): void => {
    setView(next);
    setState(next.state);
    setDueOn(next.dueOn ?? "");
    setGraceDays(String(next.graceDays));
    setReason("");
  };

  useEffect(() => {
    void clinicSubscription(clinicId)
      .then(load)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load the subscription");
      });
  }, [clinicId]);

  const days = Number(graceDays);
  const dueOk = dueOn === "" || /^\d{4}-\d{2}-\d{2}$/.test(dueOn);
  const daysOk = Number.isInteger(days) && days >= 1 && days <= 365;

  return (
    <section className={styles.panel} data-testid="subscription-panel">
      <span className={styles.panelTitle}>Subscription</span>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {view === null ? (
        <span className={styles.muted}>Loading…</span>
      ) : (
        <>
          <span className={styles.muted} data-testid="subscription-summary">
            {SUBSCRIPTION_LABELS[view.state]}
            {view.state === "GRACE" && view.graceEndsOn !== null && `, until ${view.graceEndsOn}`}
            {view.dueOn !== null && ` · due ${view.dueOn}`}
            {view.daysOverdue > 0 && ` · ${String(view.daysOverdue)} days overdue`}
            {view.changedByName !== null &&
              view.changedAt !== null &&
              ` · last moved by ${view.changedByName} on ${view.changedAt.slice(0, 10)}`}
          </span>
          {/*
            **Every box says what it is** (Emile, 2026-10-17: *"the date needs to
            open a calendar, what is the 14 for? dropdown can be prettier"*). The
            four were bare inputs in a row, the grace length an unlabelled "14".
          */}
          <div className={styles.subscriptionForm}>
            <Select
              id={`subscription-state-${clinicId}`}
              label="State"
              required
              value={state}
              options={SUBSCRIPTION_STATES.map((s) => ({
                value: s,
                label: SUBSCRIPTION_LABELS[s],
              }))}
              onChange={(next) => {
                setState(next as SubscriptionState);
              }}
              testId="subscription-state"
            />
            <label className={styles.field}>
              <span>
                Payment due
                <RequiredMark />
              </span>
              <input
                type="date"
                value={dueOn}
                data-testid="subscription-due"
                onChange={(event) => {
                  setDueOn(event.target.value);
                }}
              />
            </label>
            <label className={styles.field}>
              <span>
                Grace lasts
                <RequiredMark />
              </span>
              <span className={styles.withUnit}>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={graceDays}
                  data-testid="subscription-grace"
                  onChange={(event) => {
                    setGraceDays(event.target.value);
                  }}
                />
                <span className={styles.unit}>days</span>
              </span>
            </label>
            <label className={styles.field}>
              <span>Why</span>
              <input
                value={reason}
                placeholder="e.g. paid by transfer"
                data-testid="subscription-reason"
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />
            </label>
            <button
              type="button"
              className={styles.ghost}
              disabled={!dueOk || !daysOk}
              data-testid="subscription-save"
              onClick={() => {
                setError(null);
                void saveSubscription(clinicId, {
                  state,
                  dueOn: dueOn === "" ? null : dueOn,
                  graceDays: days,
                  reason,
                })
                  .then(load)
                  .catch((cause: unknown) => {
                    setError(cause instanceof Error ? cause.message : "Could not save");
                  });
              }}
            >
              Save
            </button>
          </div>
          <span className={styles.muted}>
            {state === "READ_ONLY"
              ? "Read-only: everyone there can sign in and read every record, and nothing can be changed."
              : state === "GRACE"
                ? `Grace ends ${String(days)} days after you save. Nothing is restricted yet.`
                : "Active. Recording a payment is a new due date here."}{" "}
            Nothing changes by itself when a date passes.
          </span>
          {view.history.length > 0 && (
            <ul className={styles.rows} data-testid="subscription-history">
              {view.history.slice(0, 5).map((h) => (
                <li key={h.at} className={styles.row}>
                  <span className={styles.rowName}>
                    {SUBSCRIPTION_LABELS[h.fromState]} → {SUBSCRIPTION_LABELS[h.toState]}
                  </span>
                  <span className={styles.rowSub}>
                    {h.at.slice(0, 10)}
                    {h.byName !== null && ` · ${h.byName}`}
                    {h.reason !== null && ` · ${h.reason}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
